import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef } from "react";

import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";

const ShiftConfirmationModal = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setSelectedShiftConfirmationNotificationId = useAppStore((state) => state.setSelectedShiftConfirmationNotificationId);
    const selectedShiftConfirmationNotificationId = useAppStore((state) => state.selectedShiftConfirmationNotificationId);
    const confirmShiftConfirmationRequest = useAppStore((state) => state.confirmShiftConfirmationRequest);
    const notifications = useAppStore((state) => state.notifications);
    const employeesData = useAppStore((state) => state.employeesData);
    const modalContainerRef = useRef(null);
    const selectedNotificationRef = useRef(
        notifications.find((notification) => notification._id === selectedShiftConfirmationNotificationId) || null
    );

    const closeModal = () => {
        setSelectedShiftConfirmationNotificationId(null);
    };

    const modalData = useMemo(() => {
        const notification = selectedNotificationRef.current;
        const relatedData = notification?.relatedData || {};
        const requester = notification?.relatedUserId
            ? employeesData?.[notification.relatedUserId]
            : null;

        const fullName = requester
            ? `${requester.ime} ${requester.prezime}`
            : "Nepoznat zaposleni";

        const presenceTypeLabel = relatedData.presenceType === "remote"
            ? "Online"
            : "U firmi";

        const formattedDate = notification?.createdAt
            ? new Date(notification.createdAt).toLocaleDateString("sr-RS")
            : "-";

        return {
            title: notification?.title || "Potvrda prijave",
            fullName,
            entranceTime: relatedData.entranceTime || "-",
            formattedDate,
            presenceTypeLabel,
            checkInRoom: relatedData.checkInRoom || "-",
            attendanceLogId: relatedData.attendanceLogId || null,
            notificationId: notification?._id || null,
            rawNotification: notification
        };
    }, [employeesData]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }

            closeModal();
        };

        document.addEventListener("mousedown", handleClickOutside, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside, true);
        };
    }, []);

    const modalContent = (
        <>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0, }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: "auto" }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`shift-confirmation-modal-container ${isDarkTheme ? "dark-shift-confirmation-modal-container" : "white-shift-confirmation-modal-container"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        className={`notification-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={closeModal}
                        aria-label="Close modal"
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                    <div className="shift-confirmation-modal-content">
                        <h3>{modalData.title}</h3>
                        <p className="shift-confirmation-modal-text">
                            {modalData.fullName} je zatrazio potvrdu prijave u {modalData.entranceTime}.
                        </p>
                        <div className="shift-confirmation-modal-info-list">
                            <div><span>Nacin prijave:</span> {modalData.presenceTypeLabel}</div>
                            <div><span>Lokacija:</span> {modalData.checkInRoom}</div>
                        </div>
                        <p className="shift-confirmation-modal-date">
                            Datum: {modalData.formattedDate}
                        </p>
                        <div className="shift-confirmation-modal-buttons">
                            <button
                                type="button"
                                className={`shift-confirmation-modal-button cancel-btn ${isDarkTheme ? "dark-btn-close" : ""}`}
                                onClick={closeModal}
                            >
                                Otkaži
                            </button>
                            <button
                                type="button"
                                className={`shift-confirmation-modal-button confirm-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                onClick={async () => {
                                    closeModal();
                                    await confirmShiftConfirmationRequest({
                                        notificationId: modalData.notificationId,
                                        attendanceLogId: modalData.attendanceLogId
                                    });
                                }}
                            >
                                Potvrdi
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </>
    );

    return createPortal(modalContent, document.body);
};

export default ShiftConfirmationModal;
